import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { housingAPI } from '../services/api';

const HousingPage = () => {
  const [housing, setHousing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    price_min: 1200,
    price_max: 6500,
    types: [],
    tags: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('Newest Listings');

  useEffect(() => {
    fetchHousing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy]);

  const fetchHousing = async () => {
    setLoading(true);
    try {
      // Mapping local filters to API params
      const params = {
        min_price: filters.price_min,
        max_price: filters.price_max,
        search: searchTerm
      };
      if (filters.types.length > 0) params.type = filters.types.join(',');
      
      const response = await housingAPI.getAll(params);
      setHousing(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load housing listings');
      setLoading(false);
      console.error('Error fetching housing:', err);
    }
  };

  const toggleType = (type) => {
    setFilters(prev => ({
      ...prev,
      types: prev.types.includes(type) 
        ? prev.types.filter(t => t !== type) 
        : [...prev.types, type]
    }));
  };

  const handlePriceChange = (e) => {
    setFilters(prev => ({
      ...prev,
      price_max: parseInt(e.target.value)
    }));
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    fetchHousing();
  };

  if (loading && housing.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-on-surface-variant font-medium">Curating your soul space...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <main className="pt-20">
        {/* Hero Search Area */}
        <section className="relative bg-white border-b border-outline-variant/10 py-16">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex flex-col md:flex-row justify-between items-end gap-8">
              <div className="max-w-2xl">
                <span className="text-primary font-bold tracking-widest text-xs uppercase mb-4 block">Housing Curated</span>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-on-background mb-6">
                  Find your <span className="text-primary">soul</span> space in the city.
                </h1>
                <p className="text-lg text-on-surface-variant leading-relaxed">
                  Skip the endless scrolling. We list vetted apartments and homes that match your lifestyle and community needs.
                </p>
              </div>
              <div className="w-full md:w-auto">
                <form onSubmit={handleSearch} className="flex items-center bg-surface-container-low rounded-full px-6 py-4 shadow-sm border border-outline-variant/20 w-full md:w-[400px]">
                  <span className="material-symbols-outlined text-primary mr-3">location_on</span>
                  <input 
                    className="bg-transparent border-none w-full focus:ring-0 text-on-surface placeholder:text-on-surface-variant/60 font-medium text-sm" 
                    placeholder="Search by city or zip..." 
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button type="submit" className="bg-primary text-white p-2 rounded-full hover:brightness-110 transition-all flex items-center justify-center">
                    <span className="material-symbols-outlined text-sm">search</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-8 py-16 flex flex-col lg:flex-row gap-16">
          {/* Sidebar Filters */}
          <aside className="w-full lg:w-80 flex-shrink-0">
            <div className="sticky top-28 space-y-12">
              <div>
                <h3 className="font-headline font-bold text-xl text-on-surface mb-8">Refine Search</h3>
                
                {/* Price Range */}
                <div className="mb-10">
                  <label className="block text-xs font-bold text-on-surface-variant mb-6 uppercase tracking-[0.2em]">Monthly Rent</label>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex-1 bg-white border border-outline-variant/30 rounded-2xl p-4 shadow-sm">
                        <span className="text-[10px] text-on-surface-variant block uppercase font-bold mb-1">Min</span>
                        <span className="text-lg font-bold text-primary">${filters.price_min.toLocaleString()}</span>
                      </div>
                      <div className="flex-1 bg-white border border-outline-variant/30 rounded-2xl p-4 shadow-sm">
                        <span className="text-[10px] text-on-surface-variant block uppercase font-bold mb-1">Max</span>
                        <span className="text-lg font-bold text-primary">${filters.price_max.toLocaleString()}</span>
                      </div>
                    </div>
                    <input 
                      className="w-full h-1 bg-surface-container rounded-full appearance-none cursor-pointer custom-range" 
                      type="range"
                      min="500"
                      max="10000"
                      step="100"
                      value={filters.price_max}
                      onChange={handlePriceChange}
                    />
                  </div>
                </div>

                {/* Property Type */}
                <div className="mb-10">
                  <label className="block text-xs font-bold text-on-surface-variant mb-6 uppercase tracking-[0.2em]">Home Type</label>
                  <div className="space-y-3">
                    {['Apartment', 'House', 'Room', 'Shared Housing'].map((type) => (
                      <label key={type} className="flex items-center group cursor-pointer">
                        <div className="relative flex items-center">
                          <input 
                            className="peer h-6 w-6 rounded-lg border-outline-variant text-primary focus:ring-primary/20 transition-all appearance-none border-2 checked:bg-primary checked:border-primary" 
                            type="checkbox"
                            checked={filters.types.includes(type.toLowerCase().replace(' ', '_'))}
                            onChange={() => toggleType(type.toLowerCase().replace(' ', '_'))}
                          />
                          <span className="material-symbols-outlined absolute text-white text-sm scale-0 peer-checked:scale-100 transition-transform w-full text-center pointer-events-none">check</span>
                        </div>
                        <span className="ml-4 text-on-surface-variant group-hover:text-primary font-medium transition-colors">
                          {type === 'Apartment' ? 'Premium Apartments' : 
                           type === 'House' ? 'Urban Townhouses' :
                           type === 'Room' ? 'Private Studios' : 'Shared Spaces'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Lifestyle Tags */}
                <div className="mb-10">
                  <label className="block text-xs font-bold text-on-surface-variant mb-6 uppercase tracking-[0.2em]">Lifestyle Needs</label>
                  <div className="flex flex-wrap gap-2">
                    {['Pet Friendly', 'Workspace', 'Smart Home', 'Pool', 'EV Charging'].map(tag => (
                      <button 
                        key={tag}
                        className={`px-4 py-2 rounded-full text-[11px] font-bold transition-all ${
                          filters.tags.includes(tag) 
                            ? 'bg-primary text-white shadow-md shadow-primary/10' 
                            : 'bg-white border border-outline-variant/30 text-on-surface-variant hover:border-primary hover:text-primary'
                        }`}
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
                        }))}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Specialist CTA */}
              <div className="bg-gradient-to-br from-primary to-primary-container p-8 rounded-[2rem] text-on-primary shadow-2xl shadow-primary/20 relative overflow-hidden group">
                <span className="absolute -right-4 -bottom-4 material-symbols-outlined text-8xl text-white/10 rotate-12 group-hover:rotate-0 transition-transform duration-500">support_agent</span>
                <h4 className="font-bold text-lg mb-2 relative z-10">Consult a Local</h4>
                <p className="text-sm opacity-80 mb-6 relative z-10">Chat with a community curator about this specific neighborhood's vibe.</p>
                <button className="w-full bg-white text-primary py-3.5 rounded-2xl font-bold hover:shadow-lg hover:scale-[1.02] transition-all relative z-10">Talk to an Expert</button>
              </div>
            </div>
          </aside>

          {/* Results Section */}
          <section className="flex-1">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4">
              <div>
                <h2 className="text-3xl font-bold text-on-surface tracking-tight mb-2">Editor's Picks</h2>
                <p className="text-on-surface-variant">Showing {housing.length} highly-rated properties in Romania</p>
              </div>
              <div className="flex items-center gap-3 bg-white px-6 py-2.5 rounded-full border border-outline-variant/20 shadow-sm">
                <span className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest">Sort By</span>
                <select 
                  className="bg-transparent border-none focus:ring-0 text-sm font-bold text-primary py-0 cursor-pointer appearance-none pr-4"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option>Newest Listings</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                </select>
              </div>
            </div>

            {/* Listings Grid */}
            {error ? (
              <div className="bg-error-container text-on-error-container p-6 rounded-3xl">
                {error}
              </div>
            ) : housing.length === 0 ? (
              <div className="text-center py-20 bg-surface-container-low rounded-[2.5rem] border border-dashed border-outline-variant/50">
                <span className="material-symbols-outlined text-6xl text-on-surface-variant/30 mb-4">search_off</span>
                <p className="text-on-surface-variant font-medium text-lg">No soul spaces found matching your criteria.</p>
                <button 
                  onClick={() => setFilters({ price_min: 1200, price_max: 6500, types: [], tags: [] })}
                  className="mt-6 text-primary font-bold hover:underline"
                >
                  Reset all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {housing.map((item) => (
                  <Link 
                    key={item.housing_id}
                    to={`/housing/${item.housing_id}`}
                    className="group bg-white rounded-[2.5rem] overflow-hidden border border-outline-variant/10 hover:shadow-[0_40px_80px_-20px_rgba(0,105,72,0.12)] hover:-translate-y-2 transition-all duration-500 flex flex-col"
                  >
                    <div className="relative h-72 overflow-hidden">
                      <img 
                        alt={item.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                        src={item.image_urls?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&q=80&w=800'}
                      />
                      <div className="absolute top-6 left-6 flex gap-2">
                        <span className="bg-white/90 backdrop-blur-md text-primary px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm">Vetted</span>
                        <span className="bg-primary text-white px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm">Top Rated</span>
                      </div>
                      <button className="absolute top-6 right-6 bg-white/20 backdrop-blur-md hover:bg-white text-white hover:text-red-500 p-3 rounded-full transition-all shadow-xl border border-white/30">
                        <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: "'FILL' 0" }}>favorite</span>
                      </button>
                    </div>
                    <div className="p-10 flex-grow">
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex-1 mr-4">
                          <h3 className="text-2xl font-bold text-on-surface group-hover:text-primary transition-colors leading-tight mb-2 line-clamp-1">
                            {item.title}
                          </h3>
                          <div className="flex items-center text-on-surface-variant text-sm font-medium">
                            <span className="material-symbols-outlined text-primary text-base mr-1.5">location_on</span>
                            <span className="line-clamp-1">{item.location_address}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-2xl font-extrabold text-primary">${item.price}</span>
                          <span className="text-[10px] block font-bold text-on-surface-variant uppercase tracking-tighter mt-1">per month</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mb-8 py-6 border-y border-outline-variant/10">
                        <div className="flex flex-col items-center gap-1">
                          <span className="material-symbols-outlined text-primary">bed</span>
                          <span className="text-sm font-bold">{item.bedrooms} Bed</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 border-x border-outline-variant/10">
                          <span className="material-symbols-outlined text-primary">bathtub</span>
                          <span className="text-sm font-bold">{item.bathrooms || 1} Bath</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <span className="material-symbols-outlined text-primary">square_foot</span>
                          <span className="text-sm font-bold">{item.area_sqm} m²</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.amenities?.slice(0, 2).map((amenity, idx) => (
                          <span key={idx} className="px-3 py-1.5 rounded-lg bg-emerald-50 text-primary text-[10px] font-bold border border-emerald-100 uppercase">
                            {amenity}
                          </span>
                        )) || (
                          <>
                            <span className="px-3 py-1.5 rounded-lg bg-emerald-50 text-primary text-[10px] font-bold border border-emerald-100 uppercase">Pet Friendly</span>
                            <span className="px-3 py-1.5 rounded-lg bg-emerald-50 text-primary text-[10px] font-bold border border-emerald-100 uppercase">Solar Ready</span>
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            <div className="mt-20 flex justify-center">
              <button className="px-12 py-5 rounded-full border-2 border-primary text-primary font-bold text-lg hover:bg-primary hover:text-white transition-all active:scale-95 shadow-xl shadow-primary/10">
                Load More curated spaces
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default HousingPage;
